import React, { useContext, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  brandColours,
  fontSize,
  visuallyHidden,
} from '../styles';
import { Container, Link, IconButton } from './ui';
import Logo from './Logo';
import Search from './Search';
import Navigation from './Navigation';
import { StoreContext } from '../context/StoreContext';
import personIcon from '../images/person-icon.inline.svg';
import basketIcon from '../images/basket-icon.inline.svg';

const StyledHeader = styled.header``;

const StyledTop = styled.div`
  padding: 14px 0;
  background-color: ${brandColours.primary};
`;

const StyledTopInner = styled.div`
  ${maxBreakpointQuery.medium`
    text-align: center;
  `}

  ${minBreakpointQuery.medium`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `}
`;

const StyledSitewideNotice = styled.div`
  p {
    color: ${standardColours.black};
    ${fontSize(12)};

    ${minBreakpointQuery.small`
      ${fontSize(13)};
    `}

    ${minBreakpointQuery.large`
      ${fontSize(14)};
    `}
  }
`;

const StyledPhoneNav = styled.div`
  ${minBreakpointQuery.medium`
    display: flex;
    align-items: center;
  `}
`;

const StyledPhone = styled.a`
  color: ${standardColours.black};
  ${fontSize(13)};

  ${maxBreakpointQuery.medium`
    margin-top: 10px;
  `}

  ${minBreakpointQuery.large`
    ${fontSize(14)};
  `}
`;

const StyledNavList = styled.ul`
  ${maxBreakpointQuery.medium`
    ${visuallyHidden()};
  `}
`;

const StyledNavItem = styled.li`
  ${minBreakpointQuery.medium`
    display: inline-block;
    margin-left: 10px;
    padding-left: 10px;
    border-left: 1px solid ${standardColours.black};
  `}

  ${minBreakpointQuery.large`
    margin-left: 12px;
    padding-left: 12px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-left: 14px;
    padding-left: 14px;
  `}
`;

const StyledNavLink = styled(Link)`
  ${minBreakpointQuery.medium`
    color: ${standardColours.black};
    ${fontSize(13)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(14)};
  `}
`;

const StyledBottom = styled.div``;

const StyledBottomInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0;

  ${minBreakpointQuery.small`
    margin-top: 26px;
    margin-bottom: 26px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 32px;
    margin-bottom: 32px;
  `}
`;

const StyledLogo = styled(Logo)``;

const StyledSearch = styled(Search)`
  ${maxBreakpointQuery.mlarge`
    ${visuallyHidden()};
  `}
`;

const StyledIconButtons = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIconButton = styled(IconButton)`
  position: relative;
  margin-left: 10px;
  cursor: pointer;

  ${minBreakpointQuery.small`
     margin-left: 15px;
  `}

  ${minBreakpointQuery.large`
     margin-left: 20px;
  `}

  &:first-child {
    margin-left: 0;
  }
`;

const StyledText = styled.span`
  display: block;
  margin-top: 6px;

  ${minBreakpointQuery.small`
    margin-top: 8px;
  `}
`;

const StyledQuantity = styled.span`
  position: absolute;
  top: 0;
  left: calc(50% + 3px);
  display: flex;
  justify-content: center;
  align-items: center;
  height: 16px;
  width: 16px;
  color: ${standardColours.white};
  ${fontSize(9)};
  background-color: ${brandColours.tertiary};
  border-radius: 50%;
`;

const StyledNavToggle = styled.button`
  ${maxBreakpointQuery.mlarge`
    position: relative;
    height: 100%;
    background: none;
    border: none;
  `}

  ${minBreakpointQuery.mlarge`
    display: none;
  `}

  span {
    ${maxBreakpointQuery.mlarge`
      display: block;
      height: 2px;
      width: 36px;
      margin-top: 10px;
      margin-bottom: 10px;
      background-color: ${standardColours.black};
    `}

    ${maxBreakpointQuery.small`
      height: 1px;
      width: 28px;
      margin-top: 9px;
      margin-bottom: 9px;
    `}
  }
`;

const Header = () => {
  const {
    datoCmsHeader: { sitewideNotice, links },
    datoCmsContactInformation: { phone },
  } = useStaticQuery(graphql`
    query HeaderQuery {
      datoCmsHeader {
        sitewideNotice
        links {
          text
          page {
            ...LinkFragment
          }
        }
      }
      datoCmsContactInformation {
        phone
      }
    }
  `);

  const { toggleCartOpen, checkout } = useContext(StoreContext);

  const [displayNav, setDisplayNav] = useState(false);

  return (
    <StyledHeader>
      <StyledTop>
        <Container>
          <StyledTopInner>
            <StyledSitewideNotice
              dangerouslySetInnerHTML={{ __html: sitewideNotice }}
            />
            <StyledPhoneNav>
              <StyledPhone href={`tel:${phone}`}>{phone}</StyledPhone>
              <StyledNavList>
                {links.map(({ text, page }, id) => (
                  <StyledNavItem key={id}>
                    <StyledNavLink to={page}>{text}</StyledNavLink>
                  </StyledNavItem>
                ))}
              </StyledNavList>
            </StyledPhoneNav>
          </StyledTopInner>
        </Container>
      </StyledTop>
      <StyledBottom>
        <Container>
          <StyledBottomInner>
            <StyledNavToggle
              aria-label="Open / Close Navigation"
              onClick={() => setDisplayNav(!displayNav)}
            >
              <span />
              <span />
              <span />
            </StyledNavToggle>
            <StyledLogo />
            <StyledSearch />
            <StyledIconButtons>
              <StyledIconButton
                href={`https://${process.env.GATSBY_CUSTOM_SHOPIFY_STORE_URL}/account`}
                icon={personIcon}
              >
                <StyledText>Account</StyledText>
              </StyledIconButton>
              <StyledIconButton icon={basketIcon} onClick={toggleCartOpen}>
                <StyledQuantity>{checkout.lineItems.length}</StyledQuantity>
                <StyledText>Basket</StyledText>
              </StyledIconButton>
            </StyledIconButtons>
          </StyledBottomInner>
        </Container>
      </StyledBottom>
      <Navigation displayNav={displayNav} setDisplayNav={setDisplayNav} />
    </StyledHeader>
  );
};

export default Header;
